import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/** Who may see "Reset 2FA" for a row: super admins for anyone, partner admins for partner users (server re-checks scope). */
export function canResetMfa(me, u) {
  if (!me || !u || !u.mfa_enabled || u.user_id === me.user_id || me.masqueraded_by) return false;
  if (me.role === "super_admin") return true;
  return me.role === "partner_admin" && u.base_role === "partner";
}

/** Confirm + clear one account's authenticator and backup codes. Render outside menus. */
export function ResetMfaDialog({ user, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  if (!user) return null;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${user.user_id}/reset-mfa`);
      toast.success(`2FA reset for ${user.name || user.email} — signed out everywhere, notice e-mail ${data.email}.`, { duration: 7000 });
      onClose();
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset 2FA"); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="reset-mfa-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Reset 2FA for {user.name || user.email}?</AlertDialogTitle>
          <AlertDialogDescription>
            Removes their authenticator app and backup codes, signs them out on every device and e-mails them that it happened. They sign in again with their e-mail and password and can set up 2FA again (Conduit asks them to if it's required). Only do this once you're sure it's really them asking.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="reset-mfa-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="reset-mfa-confirm">
            {busy ? "Resetting…" : "Reset 2FA"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
